if (!window.PinchZoom) {
  window.PinchZoom = class PinchZoom {
    constructor(modal) {
      this.modal = modal;
      this.el = modal;

      this.scale = 1;
      this.posX = 0;
      this.posY = 0;
      this.lastDist = 0;
      this.isPanning = false;
      this.startTouchX = 0;
      this.startTouchY = 0;
      this.startPosX = 0;
      this.startPosY = 0;

      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);

      this.el.addEventListener('touchstart', this.onTouchStart, { passive: false });
      this.el.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.el.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    getActiveImg() {
      const activeSlide = this.modal.querySelector('.slide--active') || this.modal.querySelector('.slide');
      if (!activeSlide) return null;

      const modalImg = activeSlide.querySelector('.card__media--modal img');
      if (modalImg) return modalImg;

      return activeSlide.querySelector('.media img') || activeSlide.querySelector('img');
    }

    getDistance(t) {
      const dx = t[0].clientX - t[1].clientX;
      const dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    getMidpoint(t) {
      return {
        x: (t[0].clientX + t[1].clientX) / 2,
        y: (t[0].clientY + t[1].clientY) / 2,
      };
    }

    applyTransform() {
      const img = this.getActiveImg();
      if (!img) return;
      img.style.transform = `translate3d(${this.posX}px, ${this.posY}px, 0) scale(${this.scale})`;
    }

    onTouchStart(e) {
      const img = this.getActiveImg();
      if (!img) return;

      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        this.isPanning = false;
        this.lastDist = this.getDistance(e.touches);

        const mid = this.getMidpoint(e.touches);
        const rect = img.getBoundingClientRect();
        const ox = ((mid.x - rect.left) / rect.width) * 100;
        const oy = ((mid.y - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${ox}% ${oy}%`;
        img.style.willChange = 'transform';
        img.style.touchAction = 'none';
      } else if (e.touches.length === 1 && this.scale > 1.05) {
        e.stopPropagation();
        this.isPanning = true;
        this.startTouchX = e.touches[0].clientX;
        this.startTouchY = e.touches[0].clientY;
        this.startPosX = this.posX;
        this.startPosY = this.posY;
      }
    }

    onTouchMove(e) {
      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        const dist = this.getDistance(e.touches);
        this.scale = Math.min(Math.max(this.scale * (dist / this.lastDist), 1), 4);
        this.lastDist = dist;
        this.applyTransform();
      } else if (e.touches.length === 1 && this.isPanning && this.scale > 1.05) {
        e.preventDefault();
        e.stopPropagation();
        this.posX = this.startPosX + (e.touches[0].clientX - this.startTouchX);
        this.posY = this.startPosY + (e.touches[0].clientY - this.startTouchY);
        this.applyTransform();
      }
    }

    onTouchEnd(e) {
      this.isPanning = false;
      if (e.touches.length < 2 && this.scale < 1.05) {
        this.reset();
      }
    }

    resetForNewSlide() {
      this.modal.querySelectorAll('.slide img').forEach(img => {
        img.style.transform = '';
        img.style.transformOrigin = '';
        img.style.transition = '';
        img.style.willChange = '';
        img.style.touchAction = '';
      });
      this.scale = 1;
      this.posX = 0;
      this.posY = 0;
    }

    reset() {
      const img = this.getActiveImg();
      this.scale = 1;
      this.posX = 0;
      this.posY = 0;
      if (!img) return;
      img.style.transition = 'transform 0.2s ease';
      img.style.transform = 'translate3d(0, 0, 0) scale(1)';
      img.style.transformOrigin = 'center center';
      setTimeout(() => {
        if (img) img.style.transition = 'none';
      }, 200);
    }

    destroy() {
      this.resetForNewSlide();
      this.el.removeEventListener('touchstart', this.onTouchStart);
      this.el.removeEventListener('touchmove', this.onTouchMove);
      this.el.removeEventListener('touchend', this.onTouchEnd);
    }
  };
}

(() => {
  const defineGalleryModal = () => {
    if (customElements.get('gallery-modal')) return;
    const BaseModalDialog = window.ModalDialog;
    if (!BaseModalDialog) return;

    customElements.define('gallery-modal', class GalleryModal extends BaseModalDialog {
      constructor() {
        super();
        this.playVideoInModal = this.hasAttribute('data-play-video');
        this.boundKeyDown = this.handleKeyDown.bind(this);
        this.boundKeyDownOptions = { capture: true };
        this.isPreparingOpen = false;
        this.skipNextSync = false;
        this.boundRefreshEvent = this.handleRefreshEvent.bind(this);
      }

      prepareOpen() {
        if (this.isPreparingOpen) return;
        this.isPreparingOpen = true;
        this.classList.add('gallery-modal--preparing');
        this.style.pointerEvents = 'none';
      }

      finishPrepareOpen() {
        if (!this.isPreparingOpen) return;
        this.isPreparingOpen = false;
        this.classList.remove('gallery-modal--preparing');
        this.style.pointerEvents = '';
      }

      show(opener) {
        this.openedBy = opener;
        if (!this._returnFocusEl) {
          const active = document.activeElement;
          if (active && active !== document.body && active.matches(':focus-visible')) this._returnFocusEl = active;
        }
        this.prepareOpen();
        super.show(opener);
      }

      preSyncForOpener(opener) {
        if (!opener) return null;
        this.openedBy = opener;
        if (window.Shopify?.designMode) {
          const section = this.openedBy?.closest('.shopify-section')
            || document.querySelector(`#shopify-section-${this.dataset.section}`);
          this.refreshFromSection(section);
        }
        const syncPromise = this.syncToOpener({ silent: true });
        this.skipNextSync = true;
        return syncPromise;
      }

      onShowActions() {
        super.onShowActions();
        if (window.Shopify?.designMode) {
          const section = this.openedBy?.closest('.shopify-section')
            || document.querySelector(`#shopify-section-${this.dataset.section}`);
          this.refreshFromSection(section);
        }
        if (!this.skipNextSync) {
          this.syncToOpener();
        } else {
          this.skipNextSync = false;
          requestAnimationFrame(() => this.finishPrepareOpen());
        }
        window.addEventListener('keydown', this.boundKeyDown, this.boundKeyDownOptions);
        const modalSlider = this.querySelector('slider-component');
        if (modalSlider) {
          modalSlider.preloadAdjacentSlides();
          const firstButton = modalSlider.querySelector('button[name="prev"], button[name="next"]');
          if (firstButton) firstButton.focus();
        }
        trapFocus(this, this.dialog);
        this.initPinchZoom();
      }

      onCloseActions() {
        this.destroyPinchZoom();
        window.removeEventListener('keydown', this.boundKeyDown, this.boundKeyDownOptions);
        this.skipNextSync = false;
        const opener = this.openedBy;
        const returnFocusEl = this._returnFocusEl || null;
        this._returnFocusEl = null;
        this.openedBy = null;
        super.onCloseActions();
        this.openedBy = opener;
        let focusTarget = returnFocusEl;
        if (focusTarget && !focusTarget.matches('button, a, input, select, textarea, [tabindex]')) {
          focusTarget = focusTarget.querySelector('button, a, [tabindex]') || focusTarget;
        }
        if (focusTarget && typeof focusTarget.focus === 'function') {
          requestAnimationFrame(() => {
            if (focusTarget.closest('.thumbnail-list__item')) {
              focusTarget.focus({ preventScroll: true });
              return;
            }
            const opener = focusTarget.closest('modal-opener') || focusTarget.parentElement;
            const prevOpenerStyle = opener ? opener.getAttribute('style') || '' : null;
            const prevBtnStyle = focusTarget.getAttribute('style') || '';
            if (opener) opener.style.cssText = prevOpenerStyle + ';visibility:visible!important;position:fixed;inset:0 auto auto 0;width:1px;height:1px;';
            focusTarget.style.cssText = prevBtnStyle + ';visibility:visible!important;';
            focusTarget.focus({ preventScroll: true });
            requestAnimationFrame(() => {
              if (opener) opener.setAttribute('style', prevOpenerStyle);
              focusTarget.setAttribute('style', prevBtnStyle);
            });
          });
        }
      }

      connectedCallback() {
        super.connectedCallback?.();
        document.addEventListener('gallery-modal:refresh', this.boundRefreshEvent);
      }

      disconnectedCallback() {
        document.removeEventListener('gallery-modal:refresh', this.boundRefreshEvent);
        super.disconnectedCallback?.();
      }

      handleRefreshEvent(event) {
        const section = event.detail?.section;
        if (!section) return;
        if (this.id !== `GalleryModal-${section.id}`) return;
        this.refreshFromSection(section);
      }

      handleKeyDown(event) {
        if (!this.isOpen) return;
        const modalSlider = this.querySelector('slider-component');
        if (event.key === 'Escape' || event.code === 'Escape') {
          event.preventDefault();
          this.hide();
          return;
        }
        if (event.key === 'Tab') {
          const els = Array.from(this.querySelectorAll(
            "button:enabled, a[href], [tabindex]:not([tabindex^='-']), video[controls]"
          )).filter(el => {
            if (el.classList.contains('deferred-media__poster') && el.closest('[data-hide-poster]')) return false;
            const hiddenParent = el.closest('[aria-hidden="true"]');
            if (!hiddenParent) return true;
            return !!hiddenParent.closest('.slide--active');
          });
          if (els.length) {
            event.preventDefault();
            const activeIndex = els.indexOf(document.activeElement);
            if (activeIndex === -1) {
              const otherNav = els.find(el => el.name === 'prev' || el.name === 'next');
              (otherNav || els[0]).focus();
            } else if (event.shiftKey) {
              (activeIndex === 0 ? els[els.length - 1] : els[activeIndex - 1]).focus();
            } else {
              (activeIndex === els.length - 1 ? els[0] : els[activeIndex + 1]).focus();
            }
          }
          return;
        }
        if (!modalSlider || modalSlider.isDisabled) return;
        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          modalSlider.navigateToPreviousSlide();
        } else if (event.code === 'ArrowRight') {
          event.preventDefault();
          modalSlider.navigateToNextSlide();
        }
      }

      initPinchZoom() {
        this.destroyPinchZoom();
        this._pinchZoom = new window.PinchZoom(this);

        const slider = this.querySelector('slider-component');
        if (slider) {
          this._onSlideChanged = () => {
            this._pinchZoom?.resetForNewSlide();
          };
          slider.addEventListener('slideChanged', this._onSlideChanged);
        }
      }

      destroyPinchZoom() {
        this._pinchZoom?.destroy();
        this._pinchZoom = null;

        const slider = this.querySelector('slider-component');
        if (slider && this._onSlideChanged) {
          slider.removeEventListener('slideChanged', this._onSlideChanged);
          this._onSlideChanged = null;
        }
      }

      syncToOpener({ silent = false } = {}) {
        if (!this.openedBy) return Promise.resolve();
        let activeMediaId = this._preferredMediaId || this.openedBy.getAttribute('data-media-id');
        this._preferredMediaId = null;

        if (!activeMediaId) {
          const mainSlider = this.openedBy.closest('.slider') || this.openedBy.closest('slider-component');
          if (mainSlider) {
            const activeSlide = mainSlider.querySelector('.slide--active') || mainSlider.querySelector('.slide');
            if (activeSlide) {
              activeMediaId = activeSlide.getAttribute('data-media-modal-id') || activeSlide.getAttribute('data-media-id');
            }
          }
        }
        const modalSlider = this.querySelector('slider-component');

        if (modalSlider) {
          return customElements.whenDefined('slider-component').then(() => {
            if (window.Shopify?.designMode && typeof modalSlider.reInit === 'function') {
              modalSlider.reInit();
            }

            const slides = Array.from(modalSlider.querySelectorAll('.slide'));
            let targetIndex = slides.findIndex(slide => {
              const slideId = slide.getAttribute('data-media-id');
              return slideId === activeMediaId || (slideId && slideId.endsWith(activeMediaId));
            });

            if (targetIndex === -1) targetIndex = 0;

            const performUpdate = () => {
              if (silent || window.Shopify?.designMode) {
                modalSlider.enableTransition?.(false);
              }
              modalSlider.setSlideWidth?.();
              modalSlider.goToSlide?.(targetIndex, false);
              modalSlider.updatePosition?.();
            };

            requestAnimationFrame(() => {
              if (!silent) {
                this.finishPrepareOpen();
              }
              void modalSlider.offsetWidth;
              requestAnimationFrame(() => {
                if (!modalSlider.isDisabled) {
                  modalSlider.isInitialized = false;
                  modalSlider.handleDeferredInit?.();
                }

                performUpdate();
                this.finishPrepareOpen();
                if (this.playVideoInModal) {
                  const activeSlide = slides[targetIndex];
                  if (activeSlide) {
                    const deferredMedia = activeSlide.querySelector('deferred-media');
                    if (deferredMedia) deferredMedia.loadContent(true);
                  }
                }
                trapFocus(this, this.dialog);
              });
            });
          });
        } else if (this.playVideoInModal && !silent) {
          const deferredMedia = this.querySelector('deferred-media');
          if (deferredMedia) {
            customElements.whenDefined('deferred-media').then(() => {
              requestAnimationFrame(() => {
                deferredMedia.loadContent(true);
              });
            });
          }
        }
        return Promise.resolve();
      }

      refreshFromSection(section) {
        if (!window.Shopify?.designMode || !section) return;

        const sourceList = section.querySelector('[data-gallery-modal-source]');
        const modalList = this.querySelector('.gallery__media-list');
        if (!sourceList || !modalList) return;

        modalList.innerHTML = sourceList.innerHTML;

        const modalSlider = this.querySelector('slider-component');
        if (modalSlider?.sliderTrack) {
          modalSlider.sliderItems = modalSlider.sliderTrack.querySelectorAll(':scope > .slide');
        }
      }
    });
  };

  if (window.ModalDialog) {
    defineGalleryModal();
    return;
  }

  document.addEventListener('modal-dialog:ready', defineGalleryModal, { once: true });
})();